import { Schema } from "effect"
import { AgentPatch, AreaPatch, GoalPatch, KpiPatch } from "./commands/graph"
import { CommandRejection, EventRef } from "./commands/index"
import { Actor, AgentKind, EntityRef, Evidence, Hold, Principal, RankedRef, Taint } from "./common"
import { WorkAssignment, WorkReply, WorkRequest, WorkVerification } from "./coordination"
import { Monitor, WakeReason } from "./domain/agentlife"
import { BriefSections } from "./domain/brief-sections"
import {
  Assignee,
  BlockReason,
  CloseResult,
  DoneCheck,
  Edge,
  Item,
  ItemPatch,
  ItemStatus,
  LegacyInitiativePatch,
  Resolution,
  Review,
  ReviewStage,
  V1InitiativeStatus,
} from "./domain/item"
import { Artifact, Comment, MemoryItem } from "./domain/knowledge"
import { Agent, Area, Charter, IntegrationGrant, Mind, Policy, PolicyDocument } from "./domain/org"
import { BetVerdictValue, Goal, Kpi, KpiEvidence, KpiSample, MetricCondition } from "./domain/plan"
import { Approver, AskedReason, Proposal, ProposalBody, RelayedBy } from "./domain/proposal"
import { MessageDeliveryState, MessageParty, MessageRoute, Thread } from "./domain/thread"
import { Integration, Member, ModelCredential, Project, Repo } from "./domain/workspace"
import { GithubLogin, GitSha, MergeResult, MergeVia, PrFact, PrReviewSource, PrSnapshot } from "./github"
import {
  AgentId,
  AreaId,
  ArtifactId,
  ChangesetId,
  CommandId,
  CommentId,
  CredentialId,
  EdgeId,
  Epoch,
  GoalId,
  IntegrationId,
  ItemId,
  KpiId,
  Markdown,
  MemoryItemId,
  MergeId,
  Millis,
  MonitorId,
  NonNegInt,
  PolicyId,
  PosInt,
  ProfileId,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  Seq,
  Sha256,
  ShortRef,
  UserId,
  WakeId,
  WorkRequestId,
} from "./ids"
import { LendCandidate, Unmet } from "./placement"
import { RunResources } from "./resources"
import {
  Downgrade,
  Executor,
  Handoff,
  RateLimitReset,
  Run,
  RunError,
  RunHead,
  RunOutcome,
  RunPlace,
  SessionRef,
  StopReason,
  Usage,
  Verdict,
} from "./run"
import { RunnerAdminState, RunnerManifest, RunnerRecord } from "./runner"
import { ComputeUsage, FundedBy } from "./usage"

// #region event-meta
/** Which policy revision allowed an event. Rolling a policy back never rewrites what was allowed at the time. */
export const PolicyStamp = Schema.Struct({
  policyId: Schema.NullOr(PolicyId),
  revision: Schema.Int,
  verdict: Schema.Literals(["allow", "propose", "exempt"]), // exempt: people, and parts of an approved proposal
  rule: Schema.NullOr(Schema.String), // e.g. "allow.create_item" (v1 events keep their "allow.create_task")
})
export type PolicyStamp = typeof PolicyStamp.Type

/** Shared by every log. Every event explains itself: who, why, as part of what, under which rule, approved by whom, how trustworthy. */
export const EventMeta = Schema.Struct({
  seq: Seq,
  ts: Millis, // stamped by the owning DO at append
  /**
   * The payload schema version the event was WRITTEN with (decision 0036): 1 = before the work-model cutover (decoded
   * with the frozen v1 schemas and upcast), 2 = after. The payload is always the current shape once decoded.
   */
  v: Schema.Int,
  actor: Actor,
  commandId: Schema.NullOr(CommandId),
  causedBy: Schema.NullOr(Schema.Union([CommandId, EventRef])),
  links: Schema.Array(EventRef),
  correlationId: Schema.String,
  changesetId: Schema.NullOr(ChangesetId),
  policy: Schema.NullOr(PolicyStamp),
  approvedBy: Schema.NullOr(Schema.Struct({ proposalId: ProposalId, by: Actor })),
  taint: Taint, // the actor's run taint at append time; projections render tainted free text as quoted data
})
export type EventMeta = typeof EventMeta.Type
// #endregion event-meta

// #region project-events
const before = <S extends Schema.Top>(s: S) => s // readability marker: "the previous value", for cheap inverses

// Decision 0093: RelayedBy lives with the proposal record (0116 keeps it on the resolution too); re-exported here.
export { RelayedBy } from "./domain/proposal"

/** How an item got to its new status. One event type with a typed cause keeps evolve trivial. */
export const StatusCause = Schema.TaggedUnion({
  attempt_started: { runId: RunId },
  attempt_ended: {
    runId: RunId,
    stopReason: StopReason,
    outcome: Schema.String,
    retry: Schema.NullOr(Schema.Struct({ at: Millis, pinProfile: Schema.NullOr(ProfileId) })),
    /**
     * Wave A §6: this attempt made progress (a new artifact or a push) and is not counted toward the unattended cap.
     * Absent on every event before this wave.
     */
    progressed: Schema.optionalKey(Schema.Literal(true)),
  },
  review: {
    round: Schema.Int,
    stage: ReviewStage,
    verdict: Schema.Literals(["approve", "changes_requested", "pass", "fail", "inconclusive"]),
  },
  blocked: {},
  unblocked: { note: Schema.String },
  retry: { note: Schema.NullOr(Schema.String) },
  completed: { evidence: Schema.Array(Evidence) },
  impossible_accepted: { note: Schema.String },
  cancelled: { reason: Schema.String },
  reopened: { reason: Schema.String },
  /** A CloseItem{done} (was CompleteTask) moved an open item into review, or closed a container (decision 0032). */
  closed: {
    note: Schema.String,
    evidence: Schema.Array(Evidence),
    /** Decision 0101: what the close said it delivered (`CloseItem.result`). */
    result: Schema.optionalKey(CloseResult),
    /** Decision 0101: a person's close (or a close a person approved) skipped these review stages. */
    skipped: Schema.optionalKey(Schema.Array(ReviewStage)),
  },
  /** A planning move (MoveItem): triage / backlog / todo / in_progress. */
  planned: {},
  /**
   * Decision 0059: the PR was merged. `via: "github"` (a person on GitHub) or `"minga"` (a person's Merge in Minga) pass
   * the review; `"run"` (a Minga run with the person's gh) appears only on an item with no human stage. `skipped`: the
   * review stages the merge skipped without a verdict.
   */
  merged: {
    url: Schema.String,
    number: PosInt,
    sha: Schema.NullOr(GitSha),
    login: Schema.NullOr(GithubLogin),
    via: MergeVia,
    by: Schema.optionalKey(UserId),
    skipped: Schema.optionalKey(Schema.Array(ReviewStage)),
  },
  /** Decision 0059: the PR was closed on GitHub without merging; the item goes back to its agent. */
  pr_closed: { url: Schema.String, number: PosInt },
})
export type StatusCause = typeof StatusCause.Type

export const ProjectEvent = Schema.TaggedUnion({
  ProjectWorkRequested: { request: WorkRequest },
  ProjectWorkReceived: { request: WorkRequest },
  ProjectWorkCancelled: {
    requestId: WorkRequestId,
    revision: PosInt,
    note: Schema.String,
    requester: UserId,
  },
  ProjectWorkStopped: {
    requestId: WorkRequestId,
    revision: PosInt,
    note: Schema.String,
    assignment: Schema.optionalKey(WorkAssignment),
  },
  ProjectWorkLinked: { requestId: WorkRequestId, revision: PosInt, itemId: ItemId },
  ProjectWorkReplied: { requestId: WorkRequestId, reply: WorkReply },
  ProjectWorkReplyReceived: { requestId: WorkRequestId, reply: WorkReply },
  ProjectWorkVerified: { requestId: WorkRequestId, verification: WorkVerification },
  WorkDeliveryRecorded: {
    requestId: WorkRequestId,
    revision: PosInt,
    replyId: Schema.optionalKey(CommandId),
    control: Schema.optionalKey(Schema.Boolean),
    problem: Schema.NullOr(Schema.String),
  },
  // ---- genesis, settings, sync ----
  ProjectCreated: { project: Project },
  ProjectUpdated: { patch: Schema.JsonObject, before: Schema.JsonObject },
  /** Decision 0049: the brief (Project.description) changed through ReviseBrief. */
  BriefRevised: { body: Markdown, before: Markdown, why: Schema.NullOr(Schema.String) },
  WorkspaceSynced: {
    members: Schema.Array(Member),
    mayor: Schema.NullOr(Schema.Struct({ agentId: AgentId, ownerId: UserId })),
    /** Decision 0129: the person whose change to the project's members this sync carries (added, role, removed, joined). */
    changedBy: Schema.optionalKey(UserId),
  },
  PolicyCreated: { policy: Policy },
  PolicyRevised: {
    policyId: PolicyId,
    revision: Schema.Int,
    document: PolicyDocument,
    restoredFrom: Schema.NullOr(Schema.Int),
    before: before(PolicyDocument),
  },
  // ---- areas and agents ----
  AreaCreated: { area: Area },
  AreaUpdated: { areaId: AreaId, patch: AreaPatch, before: before(AreaPatch) },
  AreaCharterRevised: { areaId: AreaId, charter: Charter, before: before(Charter) },
  AreaArchived: { areaId: AreaId },
  IntegrationGranted: { areaId: AreaId, grant: IntegrationGrant },
  IntegrationRevoked: { areaId: AreaId, integrationId: IntegrationId },
  AgentHired: { agent: Agent },
  AgentUpdated: { agentId: AgentId, patch: AgentPatch, before: before(AgentPatch) },
  AgentCharterRevised: { agentId: AgentId, charter: Charter, before: before(Charter) },
  AgentMindChanged: { agentId: AgentId, mind: Mind, before: before(Mind) },
  AgentStatusChanged: {
    agentId: AgentId,
    status: Schema.Literals(["active", "paused", "retired"]),
    before: Schema.Literals(["active", "paused", "retired"]),
  },
  // ---- goals and KPIs ----
  GoalCreated: { goal: Goal },
  GoalUpdated: { goalId: GoalId, patch: GoalPatch, before: before(GoalPatch) },
  GoalStatusChanged: {
    goalId: GoalId,
    status: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
    before: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
    note: Schema.String,
  },
  TargetChanged: {
    subject: Schema.TaggedUnion({ goal: { id: GoalId }, item: { id: ItemId } }),
    condition: MetricCondition,
    before: Schema.NullOr(MetricCondition),
  },
  KpiDefined: { kpi: Kpi },
  KpiUpdated: { kpiId: KpiId, patch: KpiPatch, before: before(KpiPatch) },
  /** `evidence` (decision 0045): how the number was measured; absent on older events and on most people's samples. */
  KpiSampleRecorded: {
    kpiId: KpiId,
    sample: KpiSample,
    evidence: Schema.optionalKey(Schema.Array(KpiEvidence)),
  },
  /**
   * Decision 0098: an agent sent a person (`to`; before 0130 always its keeper) a brief at `at`. Not the project brief
   * (BriefRevised). Kept per agent and person in `ProjectState.briefsTo` (the newest only); `to` gets it as a
   * notification.
   */
  BriefSent: {
    agentId: AgentId,
    to: UserId,
    body: Markdown,
    at: Millis,
    /** Decision 0144: the brief's sections (the app renders them live). Absent: a markdown-only brief (before 0144). */
    sections: Schema.optionalKey(BriefSections),
  },
  // ---- items (decision 0035) ----
  ItemCreated: { item: Item },
  /** A v2 event whose patch has `body` bumps the item's `specRev`. */
  ItemUpdated: { itemId: ItemId, patch: ItemPatch, before: before(ItemPatch) },
  /** Re-parented. Moving under a parent clears `goalIds` (top-level items only carry goals). */
  ItemMoved: {
    itemId: ItemId,
    parentId: Schema.NullOr(ItemId),
    before: Schema.NullOr(ItemId),
    goalIds: Schema.Array(GoalId),
    beforeGoalIds: Schema.Array(GoalId),
  },
  /** `assignee` absent = unchanged (events upcast from v1 TaskAssigned). */
  ItemAssigned: {
    itemId: ItemId,
    owner: Schema.NullOr(Principal),
    before: Schema.NullOr(Principal),
    assignee: Schema.optionalKey(Schema.NullOr(Assignee)),
    beforeAssignee: Schema.optionalKey(Schema.NullOr(Assignee)),
  },
  ChecksRevised: {
    itemId: ItemId,
    checks: Schema.Array(DoneCheck),
    review: Review,
    before: Schema.Struct({ checks: Schema.Array(DoneCheck), review: Review }),
    /** Decision 0116: who wrote them (a person also when they approved the card); absent on events before 0116. */
    by: Schema.optionalKey(Schema.NullOr(Principal)),
  },
  ItemStatusChanged: {
    itemId: ItemId,
    from: ItemStatus,
    to: ItemStatus,
    cause: StatusCause,
    blocked: Schema.NullOr(BlockReason),
    /** Non-null iff `to` is cancelled. */
    resolution: Schema.NullOr(Resolution),
  },
  /** `by` (decision 0032): who paused or resumed it; absent on events before that wave (derived from the event's actor). */
  ItemPaused: {
    itemId: ItemId,
    paused: Schema.Boolean,
    by: Schema.optionalKey(Schema.NullOr(Principal)),
    note: Schema.optionalKey(Schema.NullOr(Schema.String)),
  },
  /** The overdue alarm noticed a person's item past `dueAt` while it holds others (once per due date). */
  ItemOverdue: { itemId: ItemId, dueAt: Millis },
  /** Upcast only (D9): a v1 InitiativeUpdated. Its item's status follows the v1 status and horizon. */
  LegacyInitiativeUpdated: {
    itemId: ItemId,
    patch: LegacyInitiativePatch,
    before: before(LegacyInitiativePatch),
  },
  /** Upcast only (D9): a v1 InitiativeStatusChanged. */
  LegacyInitiativeStatusChanged: {
    itemId: ItemId,
    status: V1InitiativeStatus,
    before: V1InitiativeStatus,
    reason: Schema.NullOr(Schema.String),
    workDone: Schema.Boolean,
  },
  BetVerdictRecorded: {
    itemId: ItemId,
    value: BetVerdictValue,
    by: AgentId,
    evidence: Schema.Array(Evidence),
  },
  RequestDecided: { itemId: ItemId, accepted: Schema.Boolean, note: Schema.NullOr(Schema.String) },
  ReviewRecorded: {
    itemId: ItemId,
    round: Schema.Int,
    stage: ReviewStage,
    verdict: Schema.Literals(["approve", "changes_requested", "pass", "fail", "inconclusive"]),
    notes: Schema.String,
    findings: Schema.Array(Schema.String),
    runId: Schema.NullOr(RunId),
    /** Decision 0147: the CLI family the validator ran on (absent: a person's stage, or before 0147). */
    family: Schema.optionalKey(AgentKind),
    /** Decision 0147: true when it ran on its author's family (no other family was available). Absent = no. */
    sameFamily: Schema.optionalKey(Schema.Literal(true)),
  },
  RankChanged: { ref: RankedRef, rank: Schema.String, before: Schema.String },
  // ---- edges, holds, plan versions ----
  EdgeAdded: { edge: Edge },
  EdgeRemoved: { edgeId: EdgeId, edge: Edge }, // snapshot, so the inverse needs no lookup
  HoldSet: { ref: EntityRef, hold: Hold, before: Hold },
  /** Commit marker closing every changeset. Its events are contiguous and carry meta.changesetId. */
  ChangesetApplied: {
    changesetId: ChangesetId,
    planVersion: Schema.Int, // +1 per applied changeset; execution events never bump it
    rationale: Schema.String,
    firstSeq: Seq,
    changeCount: Schema.Int,
    proposalId: Schema.NullOr(ProposalId),
    revertOf: Schema.NullOr(Schema.Int),
    undoOf: Schema.NullOr(ChangesetId),
    /** Decision 0134: a person's import of history; its events wake no agent. Absent on every other changeset. */
    import: Schema.optionalKey(Schema.Literal(true)),
  },
  // ---- proposal ledger ----
  ProposalOpened: { proposal: Proposal },
  ProposalRevised: {
    proposalId: ProposalId,
    revision: Schema.Int,
    contentHash: Sha256,
    body: ProposalBody,
    reasoning: Schema.String,
    /** Wave B2: a revised plan card's reasons, judged again for its new changes (absent: keep the old ones). */
    asked: Schema.optionalKey(Schema.Array(AskedReason).check(Schema.isMaxLength(12))),
  },
  ProposalRevisionRequested: { proposalId: ProposalId, revision: Schema.Int, note: Schema.String },
  ProposalApproved: {
    proposalId: ProposalId,
    revision: Schema.Int,
    selection: Schema.NullOr(Schema.Array(Schema.Int)),
    /** Wave O1 (decision 0061): a plan card's answers when no container received them (the resolution's note). */
    note: Schema.optionalKey(Schema.String),
    /** Wave E (decision 0063): the card's recipient when the decider is someone else ("decided by Kate for Becky"). */
    for: Schema.optionalKey(UserId),
    relayedBy: Schema.optionalKey(RelayedBy),
    /** Decision 0144: the decision's ref (D-n), minted by decide. Absent on events from before 0144. */
    decisionRef: Schema.optionalKey(ShortRef),
  },
  ProposalApplied: {
    proposalId: ProposalId,
    revision: Schema.Int,
    changesetId: Schema.NullOr(ChangesetId),
    /** Permission proposals (settings wave §4.5): "run" = allow this tool for the rest of the run; "always" (decision
     *  0053) = Codex's own exec-policy rule, only on a body that offers `options.alwaysPrefix`. */
    remember: Schema.optionalKey(Schema.Literals(["once", "run", "always"])),
  },
  ProposalApplyFailed: { proposalId: ProposalId, revision: Schema.Int, rejection: CommandRejection },
  ProposalRejected: {
    proposalId: ProposalId,
    revision: Schema.Int,
    reason: Schema.NullOr(Schema.String),
    /** Wave E (decision 0063): the card's recipient when the decider is someone else ("decided by Kate for Becky"). */
    for: Schema.optionalKey(UserId),
    relayedBy: Schema.optionalKey(RelayedBy),
    /** Decision 0144: the decision's ref (D-n), minted by decide. Absent on events from before 0144. */
    decisionRef: Schema.optionalKey(ShortRef),
  },
  ProposalAnswered: {
    proposalId: ProposalId,
    revision: Schema.Int,
    optionKey: Schema.NullOr(Schema.String),
    text: Schema.NullOr(Schema.String),
    /** Decision 0059: who decided when the event's actor did not (a merge reported by a runner names the person). */
    by: Schema.optionalKey(Actor),
    /** Decision 0059: Minga's note on how it was decided ("merged on GitHub by becky (a1b2c3d)"). */
    note: Schema.optionalKey(Schema.String),
    /** Wave E (decision 0063): the card's recipient when the decider is someone else ("decided by Kate for Becky"). */
    for: Schema.optionalKey(UserId),
    relayedBy: Schema.optionalKey(RelayedBy),
    /** Decision 0144: the decision's ref (D-n), minted by decide. Absent on events from before 0144. */
    decisionRef: Schema.optionalKey(ShortRef),
  },
  ProposalExpired: {
    proposalId: ProposalId,
    revision: Schema.Int,
    appliedDefault: Schema.NullOr(Schema.String),
  },
  ProposalWithdrawn: { proposalId: ProposalId },
  ProposalAdopted: { proposalId: ProposalId, changesetId: ChangesetId },
  /**
   * Decision 0102: the agent a card is addressed to passed it one level up (its area lead → the project lead → the
   * person), with its note (what it recommends and why). The event's actor is that agent.
   */
  ProposalEscalated: {
    proposalId: ProposalId,
    revision: Schema.Int,
    by: AgentId,
    to: Approver,
    note: Schema.String,
  },
  // ---- comments, the agent bus, artifacts ----
  CommentPosted: { comment: Comment },
  /** Decision 0142: a new thread (`T-n`), written just before its first message's `CommentPosted`. */
  ThreadOpened: { thread: Thread },
  /**
   * Decision 0142: a message reached (`queued`) or was taken by (`read`) one recipient. Written with the message for
   * every recipient (so who was woken, steered or told is in the log, and loops are visible), and again when a person's
   * external participant picks it up. `chat`: the lead's chat reply to a person's `ask_lead`, pushed to their client.
   * `excerpt`: the text an external participant gets (people's clients read it from `message.received`).
   */
  MessageDelivered: {
    commentId: Schema.NullOr(CommentId),
    threadId: Schema.NullOr(CommentId),
    chat: Schema.optionalKey(
      Schema.Struct({ agentId: AgentId, messageId: Schema.String, replyTo: Schema.String }),
    ),
    from: Schema.NullOr(MessageParty),
    to: MessageParty,
    route: MessageRoute,
    state: MessageDeliveryState,
    excerpt: Schema.optionalKey(Schema.String),
    tainted: Schema.optionalKey(Schema.Boolean),
    expectsReply: Schema.optionalKey(Schema.Boolean),
  },
  CommentEdited: { commentId: CommentId, body: Schema.String, before: Schema.String },
  ThreadResolved: { threadId: CommentId },
  AskStateChanged: {
    askId: CommentId,
    state: Schema.Literals(["answered", "escalated"]),
    by: Schema.Union([CommentId, ProposalId]),
  },
  ArtifactPosted: { artifact: Artifact },
  ArtifactStateChanged: {
    artifactId: ArtifactId,
    state: Schema.Literals(["draft", "final", "published", "rejected"]),
    before: Schema.Literals(["draft", "final", "published", "rejected"]),
  },
  /**
   * Decision 0059: what the PR watch saw. `snapshot` is stored without excerpts; an excerpt travels once, on the fact
   * that introduces its review or comment. `itemId` is in the payload so the item's history lists it.
   */
  PrStateObserved: {
    artifactId: ArtifactId,
    itemId: Schema.NullOr(ItemId),
    snapshot: PrSnapshot,
    hash: Sha256,
    facts: Schema.Array(PrFact),
  },
  /** Decision 0059: a person chose Merge on the human review card; the hub signs and delivers the order. */
  MergeRequested: {
    mergeId: MergeId,
    artifactId: ArtifactId,
    itemId: ItemId,
    proposalId: ProposalId,
    headSha: GitSha,
    by: UserId,
    notAfter: Millis,
  },
  /**
   * Decision 0059: a merge ended. A merge Minga refuses before asking GitHub (`sha_moved`) has no MergeRequested; its
   * `by` names the person who clicked.
   */
  MergeFinished: {
    mergeId: MergeId,
    artifactId: ArtifactId,
    itemId: Schema.NullOr(ItemId),
    result: MergeResult,
    sentence: Schema.NullOr(Schema.String),
    by: Schema.optionalKey(UserId),
  },
  /** Decision 0059: the human review card `proposalId` was opened for `sha`. Merge uses this SHA, never a later one. */
  PrReviewBound: {
    artifactId: ArtifactId,
    itemId: ItemId,
    proposalId: ProposalId,
    sha: GitSha,
    source: PrReviewSource,
  },
  // ---- runs (milestones only: never chunks, heartbeats or renewals) ----
  /** `branch` (fix wave §4.2): the worktree branch of the frozen job, null = scratch; absent for turns and old events. */
  RunRequested: { run: Run, branch: Schema.optionalKey(Schema.NullOr(Schema.String)) },
  /** `lendable` (wave E, decision 0063): other people's personal runners that could take it if lent to the item. */
  RunWaiting: {
    runId: RunId,
    unmet: Schema.Array(Unmet),
    since: Millis,
    lendable: Schema.optionalKey(Schema.Array(LendCandidate)),
  },
  /**
   * Re-walk 2026-10-28 (decision 0166 §2): a message of `askerId` to `agentId` waits behind another turn, and its own
   * turn would wait on this (the hub's probe). Replaces the agent's and asker's earlier one; `unmet: []` clears it.
   */
  AskWaiting: {
    agentId: AgentId,
    askerId: UserId,
    unmet: Schema.Array(Unmet),
    since: Millis,
    lendable: Schema.optionalKey(Schema.Array(LendCandidate)),
  },
  RunLeased: { runId: RunId, epoch: Epoch, executor: Executor },
  /** The lease ended before the run reached "running": the hub offers it again (next epoch). */
  RunRequeued: {
    runId: RunId,
    epoch: Epoch,
    /** runner_outdated (decision 0039): the hub took the lease back from a runner without `item_model`; no attempt counted. */
    reason: Schema.Literals(["ceiling", "prepare_failed", "shutdown", "expired", "runner_outdated"]),
    prestartReleases: Schema.Int,
  },
  RunStarted: { runId: RunId, epoch: Epoch, sessionIn: Schema.NullOr(SessionRef), taint: Taint },
  RunMilestone: {
    runId: RunId,
    epoch: Epoch,
    kind: Schema.String,
    text: Schema.String,
    ref: Schema.NullOr(Schema.String),
  },
  RunOutcomeReported: { runId: RunId, epoch: Epoch, outcome: RunOutcome },
  RunVerdictReported: { runId: RunId, epoch: Epoch, verdict: Verdict },
  RunTaintRaised: { runId: RunId, epoch: Epoch, source: Schema.String },
  RunCancelRequested: {
    runId: RunId,
    reason: Schema.String,
    requestedAt: Schema.optionalKey(Millis),
    /** Decision 0116: the run never started and its item changed place: the next attempt follows, unpaused. */
    redispatch: Schema.optionalKey(Schema.Boolean),
  },
  /** Truthful end: mechanical stop reason, the agent's claim, the accepted outcome, and any downgrade between them. */
  RunEnded: {
    runId: RunId,
    epoch: Epoch,
    stopReason: StopReason,
    claimed: Schema.NullOr(RunOutcome),
    outcome: RunOutcome,
    downgrade: Schema.NullOr(Downgrade),
    verdict: Schema.NullOr(Verdict),
    usage: Schema.Array(Usage),
    error: Schema.NullOr(RunError),
    sessionOut: Schema.NullOr(SessionRef),
    transcriptRef: Schema.NullOr(Schema.String),
    /** Decision 0059: the worktree's head (worker runs with a branch, runner ≥ 0.7.0). */
    head: Schema.optionalKey(RunHead),
    /** Wave F (decision 0062): whose plan or key paid; validated by decide against the actor. */
    fundedBy: Schema.optionalKey(FundedBy),
    /** Wave F: sandbox compute rows (later; platform runners). */
    compute: Schema.optionalKey(Schema.Array(ComputeUsage)),
    /** Decision 0095: where it ran (runner runs; absent on older events). */
    place: Schema.optionalKey(RunPlace),
    /** Decision 0149: what the run used (CPU, memory, throttling, the machine's load). Absent on older events. */
    resources: Schema.optionalKey(RunResources),
    /** Decision 0155: a `rate_limited` run's known reset (absent: not rate limited, no reset known, older events). */
    rateLimit: Schema.optionalKey(RateLimitReset),
  },
  OutwardActionExecuted: {
    proposalId: ProposalId,
    revision: Schema.Int,
    runId: RunId,
    resultRef: Schema.String,
  },
  OutwardActionFailed: { proposalId: ProposalId, revision: Schema.Int, runId: RunId, error: Schema.String },
  // ---- memory: project, area and item scopes ----
  MemoryItemCreated: { item: MemoryItem },
  MemoryItemRevised: { itemId: MemoryItemId, item: MemoryItem, beforeRevision: Schema.Int },
  MemoryItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MemoryItemRevoked: { itemId: MemoryItemId, reason: Schema.String }, // "show me every run that loaded it"
  // ---- wave C (decision 0071): monitors and the clock (decision C2: one clock, in the Project DO) ----
  /** A monitor was set (created, or replaced whole by an edit) by a person, or by the person who accepted its card. */
  MonitorSet: { monitor: Monitor },
  MonitorRemoved: { monitorId: MonitorId },
  /** Settings › Monitors' Pause (`Monitor.enabled = !paused`). */
  MonitorPaused: { monitorId: MonitorId, paused: Schema.Boolean },
  /**
   * The clock fired a monitor (system:scheduler) at `at`; `day` is that date (yyyy-mm-dd) in the project's zone, the
   * timed-turn counter's day. `held`: the agent's timed cap for the day was reached, so the wake waits on the
   * `timed:<agentId>:<day>` card instead of counting.
   */
  MonitorFired: {
    monitorId: MonitorId,
    at: Millis,
    day: Schema.String,
    held: Schema.optionalKey(Schema.Literal(true)),
  },
  /**
   * active ⇄ blocked. Blocked: the setter left the project or became a viewer (`runId: null`), or a fired turn failed.
   * `ownerId`: Resume as me (the person who resumed it becomes the setter).
   */
  MonitorStateChanged: {
    monitorId: MonitorId,
    state: Schema.TaggedUnion({
      active: {},
      blocked: { reason: Schema.String, runId: Schema.NullOr(RunId) },
    }),
    ownerId: Schema.optionalKey(UserId),
  },
  /**
   * A1, the daily check (system:scheduler): the loose-end keys open at `at`. Written only when the set changed; the
   * owners of new keys are woken with `loose_ends` (core reactions, from `keys` minus the previous `dailyCheck.keys`).
   */
  LooseEndsNoted: { at: Millis, keys: Schema.Array(Schema.String).check(Schema.isMaxLength(500)) },
  /**
   * A recurring series came due for `period` with no open occurrence (system:scheduler): its owner agent wakes with
   * `series_due`. `day` and `held` as on MonitorFired. The system creates no occurrence: the agent presses Run now.
   */
  SeriesDueNoted: {
    itemId: ItemId,
    period: Schema.String,
    day: Schema.String,
    held: Schema.optionalKey(Schema.Literal(true)),
  },
  // ---- watchdog, outcomes, privacy ----
  /** Decision 0135: a live run went quiet for `idleMs` (system:watchdog); its agent (else the lead) wakes once. */
  RunQuietNoted: { runId: RunId, idleMs: NonNegInt, at: Millis },
  StallDetected: { subject: EntityRef, signal: Schema.String, proposalId: ProposalId },
  OutcomeCheckScheduled: {
    subject: EntityRef,
    dueAt: Millis,
    kpiIds: Schema.Array(KpiId),
    evaluator: AgentId,
  }, // Phase 3
  OutcomeCheckDue: { subject: EntityRef }, // Phase 3
  /**
   * The ONE sanctioned in-place change: the referenced row's fields are overwritten with a hash marker in the same
   * transaction. In state it rewrites `fields` of every record whose content was written at `seq` (`contentSeq`),
   * still last touched by it (`lastSeq`), or named in `refs` — the DO fills `refs` from the logged event at `seq`,
   * which covers patched graph nodes whose `lastSeq` has moved on since.
   */
  Redacted: {
    seq: Seq,
    fields: Schema.Array(Schema.String),
    reason: Schema.String,
    refs: Schema.optionalKey(Schema.Array(EntityRef)),
  },
})
export type ProjectEvent = typeof ProjectEvent.Type

export const ProjectEventEnvelope = Schema.Struct({ ...EventMeta.fields, event: ProjectEvent })
export type ProjectEventEnvelope = typeof ProjectEventEnvelope.Type
// #endregion project-events

// #region other-logs
/** Workspace DO log: durable transitions only. Heartbeats, renewals and latency are mutable rows. */
export const WorkspaceEvent = Schema.TaggedUnion({
  WorkspaceInitialised: { workspaceId: Schema.String, name: Schema.String, slug: Schema.String },
  MembersChanged: { members: Schema.Array(Member) },
  ProjectRegistered: { projectId: ProjectId, slug: Schema.String, name: Schema.String },
  ProjectArchived: { projectId: ProjectId },
  RepoRegistered: { repo: Repo },
  RepoRemoved: { repoId: RepoId },
  CredentialAdded: { credential: ModelCredential },
  CredentialRemoved: { credentialId: CredentialId },
  RunnerPaired: { runner: RunnerRecord },
  RunnerManifestChanged: { runnerId: RunnerId, manifest: RunnerManifest }, // on change only
  RunnerAdminChanged: { runnerId: RunnerId, admin: RunnerAdminState, before: RunnerAdminState },
  RunnerRenamed: { runnerId: RunnerId, name: Schema.String },
  LeaseGranted: {
    runId: RunId,
    projectId: Schema.NullOr(ProjectId),
    epoch: Epoch,
    runnerId: RunnerId,
    profileId: ProfileId,
    expiresAt: Millis,
  },
  LeaseEnded: {
    runId: RunId,
    epoch: Epoch,
    reason: Schema.Literals(["final", "released", "expired", "revoked", "stale"]),
    prestart: Schema.Boolean,
  },
  MayorHired: { agent: Agent },
  MayorUpdated: { agent: Agent },
  IntegrationRegistered: { integration: Integration }, // Phase 2
  IntegrationToolsRepinned: {
    integrationId: IntegrationId,
    tools: Schema.Array(Schema.Struct({ name: Schema.String, schemaHash: Sha256 })),
  },
  MemoryItemCreated: { item: MemoryItem },
  MemoryItemRevised: { itemId: MemoryItemId, item: MemoryItem, beforeRevision: Schema.Int },
  MemoryItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MemoryItemRevoked: { itemId: MemoryItemId, reason: Schema.String },
  // Mayor turns (workspace-level runs) reuse the Run* payloads of the project log.
  RunRequested: { run: Run },
  RunEnded: { runId: RunId, epoch: Epoch, stopReason: StopReason, usage: Schema.Array(Usage) },
})

/** Agent DO log (the body): inbox, turns, notebook, monitors, session continuity. The chat transcript itself is
 * AIChatAgent storage; native session entries are mirror rows, not events. */
export const AgentEvent = Schema.TaggedUnion({
  AgentConfigSynced: { agent: Agent }, // projection pushed by the Project DO (charter, mind, policy id, status)
  WakeReceived: { wakeId: WakeId, reason: WakeReason, key: Schema.String, cause: EventRef },
  WakeSkipped: {
    key: Schema.String,
    reason: Schema.Literals(["self_caused", "duplicate", "agent_paused", "agent_retired", "agent_mention"]),
  },
  TurnRequested: { runId: RunId, wakeBatchId: WakeId, wakeIds: Schema.Array(WakeId) },
  TurnStarted: { runId: RunId, executor: Executor },
  TurnSteered: {
    runId: RunId,
    messageId: Schema.String,
    priority: Schema.Literals(["now", "next", "later"]),
  },
  TurnInterruptRequested: { runId: RunId, by: UserId },
  TurnEnded: { runId: RunId, stopReason: StopReason, sessionOut: Schema.NullOr(SessionRef) },
  NotebookItemWritten: { item: MemoryItem }, // own notebook entries are auto-accepted
  NotebookItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  // Monitors moved to the Project DO log (ProjectEvent, wave C, decision 0071).
  SessionRefUpdated: { ref: SessionRef },
  HandoffWritten: { runId: RunId, handoff: Handoff },
})

/** User DO log: personal memory and notification settings. Notifications and push subscriptions are rows. */
export const UserEvent = Schema.TaggedUnion({
  MemoryItemCreated: { item: MemoryItem },
  MemoryItemRevised: { itemId: MemoryItemId, item: MemoryItem, beforeRevision: Schema.Int },
  MemoryItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MemoryItemRevoked: { itemId: MemoryItemId, reason: Schema.String },
  NotificationPrefsChanged: {
    quietHours: Schema.NullOr(Schema.Struct({ from: Schema.String, to: Schema.String, tz: Schema.String })),
    soonBatchMinutes: Schema.Int,
    digestCron: Schema.NullOr(Schema.String),
  },
})
// #endregion other-logs
